import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { showMessage } from 'dashboard/redux/actions/messages';
import { clearInsightQueriesResults } from 'dashboard/redux/slices/insight-queries';
import { RootState } from 'dashboard/redux/store';
import {
  archiveInsightQuery,
  getInsightQueries,
} from 'dashboard/redux/thunk-actions/insight-queries';
import DashboardInsightQueriesView from 'dashboard/views/DashboardInsightQueriesView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

type DashboardInsightQueriesRouteProps = PropsFromRedux;

const DashboardInsightQueriesRoute = (props: DashboardInsightQueriesRouteProps) => {
  const { loading: loadingInsightQueries } = useApiLoader({
    loader: async () => props.getInsightQueries(),
    deps: [props.getInsightQueries],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load insight queries.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    callback: handleArchiveInsightQuery,
    loading: isLoadingArchiveInsightQuery,
    error: loadingArchiveInsightQueryError,
  } = useApiCallback(async (id: string) => props.archiveInsightQuery({ id }), []);
  useApiErrorHandler({
    error: loadingArchiveInsightQueryError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to archive insight query, please try again', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to archive insight query, please try again', MessageType.Failure),
  });

  const navigate = useNavigate();
  const handleRedirectToInsightQueryTool = () => {
    props.clearInsightQueriesResults();
    navigate(`/dashboard/insight-query-tool`);
  };

  return (
    <DashboardInsightQueriesView
      insightQueries={props.insightQueries}
      loading={loadingInsightQueries || isLoadingArchiveInsightQuery}
      handleRedirectToInsightQueryTool={handleRedirectToInsightQueryTool}
      handleArchiveInsightQuery={handleArchiveInsightQuery}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  insightQueries: state.insightQueries.queries,
});

const mapDispatchToProps = {
  archiveInsightQuery,
  clearInsightQueriesResults,
  getInsightQueries,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardInsightQueriesRoute);
